<template>
  <header class="absolute inset-x-0 top-0 z-50">
    <nav class="relative flex items-center justify-between p-6 lg:px-8 z-100" aria-label="Global">
      <div class="flex lg:flex-1">
        <a href="/" class="-m-1.5 p-1.5">
          <span class="sr-only">Port Vila International School</span>
          <img class="h-24 w-auto" :src="logoUrl" alt="Logo" />
        </a>
      </div>
      <div class="flex lg:hidden">
        <button type="button" class="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700" @click="mobileMenuOpen = true">
          <span class="sr-only">Open main menu</span>
          <Bars3Icon class="h-6 w-6" aria-hidden="true" />
        </button>
      </div>
      <div class="hidden lg:flex lg:gap-x-12">
        <div v-for="item in navItems" :key="item.name" class="relative group">
          <Popover v-if="item.submenu && item.submenu.length" class="relative">
            <PopoverButton class="text-sm font-semibold leading-6 text-gray-900">
              {{ item.name }}
            </PopoverButton>
            <transition enter-active-class="transition ease-out duration-200" enter-from-class="opacity-0 translate-y-1" enter-to-class="opacity-100 translate-y-0" leave-active-class="transition ease-in duration-150" leave-from-class="opacity-100 translate-y-0" leave-to-class="opacity-0 translate-y-1">
              <PopoverPanel class="absolute left-1/2 z-10 mt-5 flex w-screen max-w-min -translate-x-1/2 px-4">
                <div class="w-56 shrink rounded-xl bg-white p-4 text-sm font-semibold leading-6 text-gray-900 shadow-lg ring-1 ring-gray-900/5">
                  <template v-for="subItem in item.submenu" :key="subItem.name">
                    <div>
                      <a v-if="subItem.pdfUrl" 
                         :href="subItem.pdfUrl" 
                         :download="subItem.download" 
                         target="_blank" 
                         class="block p-2 hover:text-indigo-600">
                        {{ subItem.name }}
                        <span v-if="subItem.download"></span>
                      </a>
                      <router-link v-else-if="!subItem.submenu || !subItem.submenu.length" :to="subItem.href" class="block p-2 hover:text-indigo-600">{{ subItem.name }}</router-link>
                      <Popover v-else class="relative">
                        <PopoverButton class="block p-2 hover:text-indigo-600">{{ subItem.name }}</PopoverButton>
                        <PopoverPanel class="absolute left-1/2 z-10 mt-5 flex w-screen max-w-min -translate-x-1/2 px-4">
                          <div class="w-56 shrink rounded-xl bg-white p-4 text-sm font-semibold leading-6 text-gray-900 shadow-lg ring-1 ring-gray-900/5">
                            <template v-for="subSubItem in subItem.submenu" :key="subSubItem.name">
                              <a v-if="subSubItem.pdfUrl" 
                                 :href="subSubItem.pdfUrl" 
                                 :download="subSubItem.download" 
                                 target="_blank" 
                                 class="block p-2 hover:text-indigo-600">
                                {{ subSubItem.name }}
                                <span v-if="subSubItem.download"></span>
                              </a>
                              <router-link v-else :to="subSubItem.href" class="block p-2 hover:text-indigo-600">{{ subSubItem.name }}</router-link>
                            </template>
                          </div>
                        </PopoverPanel>
                      </Popover>
                    </div>
                  </template>
                </div>
              </PopoverPanel>
            </transition>
          </Popover>
          <a v-else-if="item.pdfUrl" 
             :href="item.pdfUrl" 
             :download="item.download" 
             target="_blank" 
             class="text-sm font-semibold leading-6 text-gray-900">
            {{ item.name }}
            <span v-if="item.download"></span>
          </a>
          <router-link v-else :to="item.href" class="text-sm font-semibold leading-6 text-gray-900">{{ item.name }}</router-link>
        </div>
      </div>
      <div class="hidden lg:flex lg:flex-1 lg:justify-end">
        <a href="https://go.linc-ed.com/" target="_blank" class="text-sm font-semibold leading-6 text-gray-900">HERO Log in<span aria-hidden="true">&rarr;</span></a>
      </div>
    </nav>
    <Dialog as="div" class="lg:hidden" @close="mobileMenuOpen = false" :open="mobileMenuOpen">
      <div class="fixed inset-0 z-10" />
      <DialogPanel class="fixed inset-y-0 right-0 z-10 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
        <div class="flex items-center justify-between">
          <button type="button" class="-m-2.5 rounded-md p-2.5 text-gray-700" @click="mobileMenuOpen = false">
            <span class="sr-only">Close menu</span>
            <XMarkIcon class="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        <div class="mt-6 flow-root">
          <div class="-my-6 divide-y divide-gray-500/10">
            <div class="space-y-2 py-6">
              <div class="mt-12" v-for="item in navItems" :key="item.name">
                <!-- Handle items without submenus (direct links) -->
                <a v-if="item.pdfUrl" 
                   :href="item.pdfUrl" 
                   :download="item.download" 
                   target="_blank" 
                   @click="closeMobileMenu"
                   class="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50">
                  {{ item.name }}
                  <span v-if="item.download"></span>
                </a>
                <router-link v-else-if="!item.submenu || !item.submenu.length" 
                             :to="item.href" 
                             @click="closeMobileMenu"
                             class="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50">
                  {{ item.name }}
                </router-link>
                <!-- Handle items with submenus -->
                <template v-else>
                  <p 
                    @click="toggleSubmenu(item)" 
                    class="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 cursor-pointer"
                    :class="{'bg-gray-200': item.showSubmenu, 'hover:bg-gray-50': !item.showSubmenu}"
                  >
                    {{ item.name }}
                  </p>
                  <div v-if="item.showSubmenu" class="space-y-2">
                    <div v-for="subItem in item.submenu" :key="subItem.name">
                      <a v-if="subItem.pdfUrl" 
                         :href="subItem.pdfUrl" 
                         :download="subItem.download" 
                         target="_blank" 
                         @click="closeMobileMenu"
                         class="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50">
                        {{ subItem.name }}
                        <span v-if="subItem.download"></span>
                      </a>
                      <router-link v-else-if="!subItem.submenu || !subItem.submenu.length" 
                                   :to="subItem.href" 
                                   @click="closeMobileMenu"
                                   class="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50">
                        {{ subItem.name }}
                      </router-link>
                      <p v-else 
                         @click="toggleSubmenu(subItem)" 
                         class="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50 cursor-pointer">
                        {{ subItem.name }}
                      </p>
                      <div v-if="subItem.showSubmenu" class="space-y-2">
                        <div v-for="subSubItem in subItem.submenu" :key="subSubItem.name">
                          <a v-if="subSubItem.pdfUrl" 
                             :href="subSubItem.pdfUrl" 
                             :download="subSubItem.download" 
                             target="_blank" 
                             @click="closeMobileMenu"
                             class="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50">
                            {{ subSubItem.name }}
                            <span v-if="subSubItem.download"></span>
                          </a>
                          <router-link v-else 
                                       :to="subSubItem.href" 
                                       @click="closeMobileMenu"
                                       class="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50">
                            {{ subSubItem.name }}
                          </router-link>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>
            <div class="py-6">
              <a href="#" class="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50">HERO Log in (Coming soon)</a>
            </div>
          </div>
        </div>
      </DialogPanel>
    </Dialog>
  </header>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from 'axios'
import { Dialog, DialogPanel, Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { Bars3Icon, XMarkIcon } from '@heroicons/vue/24/outline'
import logoUrl from '../assets/PVIS-logo-blue.svg'

const navItems = ref([]); // Ensure this is reactive

const fetchNavigation = async () => {
  // Directly use the VUE_APP_ prefixed variable, which Vue CLI makes available
  const accessToken = process.env.VUE_APP_CONTENTFUL_ACCESS_TOKEN;

  if (!accessToken) {
    console.error('Contentful access token is missing for navigation. Please check your environment variables.');
    // Optionally handle the error, e.g., show a message or use default nav items
    return; // Stop execution if the token is missing
  }

  try {
    const response = await axios.get(`https://cdn.contentful.com/spaces/8ayqhzh3p1ua/environments/master/entries?access_token=${accessToken}&content_type=navigation`)

    const allEntries = response.data.items;
    const assets = response.data.includes?.Asset || [];

    const entryMap = new Map(allEntries.map(item => [item.sys.id, item]));

    const processNavItem = (item) => {
      if (!item || !item.fields) {
        console.warn('Invalid item structure:', item);
        return null;
      }

      const fields = item.fields;
      let pdfUrl = null;
      let pdfFileName = null;

      if (fields.pdf && fields.pdf.sys) {
        const pdfAsset = assets.find(asset => asset.sys.id === fields.pdf.sys.id);
        if (pdfAsset && pdfAsset.fields && pdfAsset.fields.file) {
          pdfUrl = `https:${pdfAsset.fields.file.url}`;
          pdfFileName = pdfAsset.fields.file.fileName || null;
        }
      }

      return {
        name: fields.name || 'Unnamed Item',
        href: fields.link || '#',
        pdfUrl: pdfUrl,
        pdfFileName: pdfFileName,
        download: fields.download || false,
        submenu: Array.isArray(fields.submenu) ? fields.submenu.map(subItem => {
          const subEntry = entryMap.get(subItem.sys.id);
          return subEntry ? processNavItem(subEntry) : null;
        }).filter(Boolean) : []
      };
    };

    const topNavItem = allEntries.find(item => item.fields && item.fields.name === "Top Navigation");

    if (!topNavItem) {
      console.error("Top Navigation item not found");
      return;
    }

    const structuredNavItems = topNavItem.fields.submenu
      .map(subItem => {
        const subEntry = entryMap.get(subItem.sys.id);
        return subEntry ? processNavItem(subEntry) : null;
      })
      .filter(Boolean);

    navItems.value = adjustTermDates(structuredNavItems);
  } catch (error) {
    console.error('Error fetching navigation from Contentful:', error);
  }
};

// Term dates housekeeping on top of the CMS menu: retire 2025 (including the
// CMS entry mislabelled 2026 whose file is the 2025 PDF), and add the 2027 and
// 2028 PDFs served from /public. Each local entry is skipped as soon as the
// CMS provides an item for that year, so this needs no cleanup when the PDFs
// are later uploaded to Contentful.
const RETIRED_TERM_YEARS = ['2025'];
const LOCAL_TERM_DATES = [
  { year: '2027', pdfUrl: '/PVIS-Term-Dates-2027.pdf' },
  { year: '2028', pdfUrl: '/PVIS-Term-Dates-2028.pdf' },
];

const adjustTermDates = (items) => {
  const isTermDates = (it) => /term dates/i.test(it.name || '');
  const hasYear = (it, year) =>
    (it.name || '').includes(year) || (it.pdfFileName || '').includes(year);
  for (const item of items) {
    if (!item.submenu || !item.submenu.some(isTermDates)) continue;
    let sub = item.submenu.filter(
      (s) => !(isTermDates(s) && RETIRED_TERM_YEARS.some((y) => hasYear(s, y))),
    );
    for (const td of LOCAL_TERM_DATES) {
      if (!sub.some((s) => isTermDates(s) && hasYear(s, td.year))) {
        const entry = {
          name: `Term Dates ${td.year} [PDF]`,
          href: '#',
          pdfUrl: td.pdfUrl,
          pdfFileName: td.pdfUrl.slice(1),
          download: false, // opens in a new tab, like the 2026 item
          submenu: [],
        };
        // Slot directly under the last existing term-dates item (i.e. 2026,
        // then each added year in turn).
        const lastTd = sub.map(isTermDates).lastIndexOf(true);
        sub.splice(lastTd === -1 ? sub.length : lastTd + 1, 0, entry);
      }
    }
    item.submenu = sub;
  }
  return items;
};

onMounted(() => {
  fetchNavigation()
})
/* 
const navigation = reactive([
{ 
    name: 'About us', 
    href: '/about-us',
    submenu: [
      { name: 'Our Team', href: '/our-team' },
      { name: 'Our Board', href: '/our-board' },
      { name: 'Our School', href: '/our-school' },
      { name: 'Our Values', href: '/our-values' },
      { name: 'Our History', href: '/our-history' },
      { name: 'Our Community [Soon]', href: '#' },
      // Add more submenu items here...
    ]
  },
  { name: 'General Information', href: '#',
    submenu: [
      { name: 'Term Dates [PDF]', href: '../assets/PVIS-2024-Term-Dates.pdf',  download: 'true' },
      { name: 'After School Activites', href: '/asa' },
      { name: 'Disaster Preparedness', href: '/disaster-preparedness' },
      { name: 'Strategic Plan', href: '/strategic-plan' },
      { name: 'PVIS Yearbook', href: '/yearbook' },
      // Add more submenu items here...
    ] },
    { name: 'Education', href: '#',
  submenu: [
      { name: 'Yumi Strong', href: '/yumi-strong' },
      { name: 'Our Curriculum', href: '/our-curriculum' },
      { name: 'SACE', href: '/sace' },
//      { name: 'PVIS Voices', href: '/our-history' },
     // { name: 'BYOD Laptops', href: '/our-history' },
      // Add more submenu items here...
    ] },
  { name: 'Enrolment', href: '#',
  submenu: [
    { name: 'Enrolment Process', href: '/enrolment-process' },  
    { name: 'School Fees', href: '/school-fees' },   
    //{ name: 'Early Education Center - Preschool', href: '/strategic-plan' },
    //  { name: 'Primary School', href: '/our-school' },
    //  { name: 'Junior Secondary School', href: '/our-school' },
    //  { name: 'Senior Secondary School', href: '/our-history' },
    //  { name: 'Scholarships', href: '/our-history' },
      // Add more submenu items here...
    ] },
    /* { name: 'Community', href: '#',
  submenu: [
        { name: 'School Board', href: '/school-board' },
        { name: 'Year Book', href: '/year-book' },
      
      // Add more submenu items here...
    ] },
    { name: 'Employment', href: '/employment' },
  { name: 'Contact', href: '/contact' },
]) */

const toggleSubmenu = (item) => {
  item.showSubmenu = !item.showSubmenu
}

const mobileMenuOpen = ref(false)

const closeMobileMenu = () => {
  mobileMenuOpen.value = false
}
</script>